import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import cls from 'clsx';
import { genUid } from '../../../uid';
import { kebabCase } from '../../../string/kebab-case';
import SimpleBar from 'simplebar';
import { isMobile } from '../../../../theme/responsive-utils';
import { debounce } from '@github/mini-throttle';
const optionLabels = {
    Size: 'Select a size',
    'Pant Length': 'Select pant length',
    Amount: 'Select amount'
};
const ACTIVE_CTA_QUERY_SELECTOR = '.js-product-options .js-product-options-action';
const GORGIAS_CHAT_ID = '#gorgias-chat-container';
export const ProductOptionsAsRadioButtons = function({ optionNames , optionValueGroups , focusedOptionIndex , selectedVariant , selectedOptions: initiallySelectedOptions , variants , focusOnIndex , dispatch , sale  }) {
    const [uid] = useState(genUid());
    const [selectedOptions, setSelectedOptions] = useState(initiallySelectedOptions);
    const [isStickyATCVisible, setIsStickyATCVisible] = useState(false);
    const [stickyATCExtraClass, setStickyATCExtraClass] = useState(null);
    const [stickyATCLabel, setStickyATCLabel] = useState(null);
    useEffect(()=>{
        Array.prototype.forEach.call(document.querySelectorAll('[data-simplebar]'), (el)=>new SimpleBar(el, {
                autoHide: false
            }));
    });
    useEffect(()=>{
        if (selectedOptions.some((option)=>option !== null)) {
            sessionStorage.setItem('selectedOptions', JSON.stringify(selectedOptions));
        }
        selectedOptions.forEach((option, optionIndex)=>{
            if (option) {
                dispatch({
                    type: 'SelectOption',
                    payload: {
                        optionIndex,
                        optionValue: option
                    }
                });
            }
        });
    }, [
        selectedOptions,
        dispatch
    ]);
    useEffect(()=>{
        const activeButton = document.querySelector(ACTIVE_CTA_QUERY_SELECTOR);
        if (activeButton instanceof HTMLElement) {
            setStickyATCLabel(activeButton.innerText);
        }
    }, [
        selectedOptions
    ]);
    useEffect(()=>{
        const handleScroll = ()=>{
            const activeButton = document.querySelector(ACTIVE_CTA_QUERY_SELECTOR);
            if (activeButton instanceof HTMLElement) {
                const activeButtonOffsetTop = activeButton.offsetTop - 70 + 48 // 70 is the rough height of the header, 48 is the rough height of the cta
                ;
                const shouldStickyATCBeVisible = window.scrollY > activeButtonOffsetTop;
                setIsStickyATCVisible(shouldStickyATCBeVisible);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return ()=>{
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);
    useEffect(()=>{
        if (isStickyATCVisible) {
            const activeButton = document.querySelector(ACTIVE_CTA_QUERY_SELECTOR);
            if (!activeButton) {
                return;
            }
            const activeButtonStyleClass = Array.from(activeButton.classList).filter((className)=>className.includes('ProductForm'));
            if (activeButtonStyleClass[0]) {
                setStickyATCExtraClass(activeButtonStyleClass[0]);
            }
        }
    }, [
        isStickyATCVisible
    ]);
    // START: Hide Gorgias chat when sticky ATC is visible
    useEffect(()=>{
        const updateChatVisibility = ()=>{
            const chat = document.querySelector(GORGIAS_CHAT_ID);
            if (!chat || !(chat instanceof HTMLElement)) {
                return;
            }
            if (isMobile()) {
                chat.style.display = isStickyATCVisible ? 'none' : '';
            } else {
                chat.style.display = '';
            }
        };
        const debouncedUpdate = debounce(updateChatVisibility, 150);
        debouncedUpdate();
        const observer = new MutationObserver(()=>{
            updateChatVisibility();
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        window.addEventListener('resize', debouncedUpdate);
        return ()=>{
            debouncedUpdate.cancel();
            observer.disconnect();
            window.removeEventListener('resize', debouncedUpdate);
        };
    }, [
        isStickyATCVisible
    ]);
    // END: Hide Gorgias chat when sticky ATC is visible
    const pageTitle = document.title.toLowerCase();
    const isEgift = pageTitle.includes('e-gift');
    const isGiftCard = pageTitle.includes('e-gift card') || pageTitle.includes('gift card');
    const firstOptionWithoutSelectedValueIndex = optionValueGroups.findIndex((_, optionValueGroupIndex)=>{
        return selectedOptions[optionValueGroupIndex] === null;
    });
    const firstOptionWithoutSelectedValue = firstOptionWithoutSelectedValueIndex !== -1 ? optionNames[firstOptionWithoutSelectedValueIndex] : null;
    const isSelectedVariantAvailable = selectedVariant && selectedVariant.available && selectedOptions[0] === selectedVariant.option1 && selectedOptions[1] === selectedVariant.option2 && selectedOptions[2] === selectedVariant.option3;
    const isStoredSelectionValidForCurrentProduct = variants.some((variant)=>{
        return selectedOptions[0] === variant.option1 && selectedOptions[1] === variant.option2 && selectedOptions[2] === variant.option3;
    });
    // Find any variant that matches the specified option at the given index
    const findVariantWithMatchingOption = (variantList, optionValue, optionIndex)=>{
        return variantList.find((variant)=>{
            const variantOption = variant.options[optionIndex];
            return variantOption === optionValue;
        }) || null;
    };
    const renderOptionGroup = (optionGroup, optionIndex, optionName)=>{
        return optionGroup.map((optionValue)=>{
            const name = kebabCase(`${uid}-${optionName}`);
            const id = kebabCase(`${name}-${optionValue.name}`);
            const isSelected = selectedOptions.includes(optionValue.name);
            const checked = isSelected;
            const disabled = optionValue.status === 'unavailable';
            const dimmed = optionValue.status === 'unavailable' || optionValue.status === 'out-of-stock';
            const labelText = optionValue.name.trim();
            // On Sale computation — apply ONLY to the Size option
            const isSizeOption = optionName.toLowerCase() === 'size';
            let isOnSale = false;
            if (isSizeOption && sale.enabled) {
                // For size options, find any variant that has this size value on sale
                const matchingVariant = findVariantWithMatchingOption(variants, optionValue.name, optionIndex);
                if (matchingVariant) {
                    isOnSale = !!sale.variantsMap[String(matchingVariant.id)];
                }
            }
            return h('div', {
                key: id,
                className: 'ProductRadioButtons-item',
                tabindex: '0',
                onKeyUp: function(e) {
                    if (e.keyCode === 13) {
                        if (disabled) {
                            return;
                        }
                        setSelectedOptions((prevOptions)=>{
                            const options = [
                                ...prevOptions
                            ];
                            options[optionIndex] = optionValue.name;
                            return options;
                        });
                        dispatch({
                            type: 'SelectOption',
                            payload: {
                                optionIndex,
                                optionValue: optionValue.name
                            }
                        });
                    }
                }
            }, h('input', {
                id,
                name,
                checked,
                disabled,
                className: 'ProductRadioButtons-itemRadio',
                type: 'radio',
                value: optionValue.name,
                'aria-label': `${optionName} ${optionValue.name}`,
                onFocus: function() {
                    focusOnIndex(optionIndex);
                },
                onClick: function() {
                    focusOnIndex(optionIndex);
                },
                onInput: function() {
                    if (disabled) {
                        return;
                    }
                    setSelectedOptions((prevOptions)=>{
                        const options = [
                            ...prevOptions
                        ];
                        options[optionIndex] = optionValue.name || null;
                        return options;
                    });
                    dispatch({
                        type: 'SelectOption',
                        payload: {
                            optionIndex,
                            optionValue: optionValue.name
                        }
                    });
                }
            }), h('label', {
                htmlFor: id,
                className: cls('ProductRadioButtons-itemLabel', {
                    'ProductRadioButtons-itemLabel--checked': checked,
                    'ProductRadioButtons-itemLabel--disabled': disabled,
                    'ProductRadioButtons-itemLabel--dimmed': dimmed
                })
            }, h('span', {
                className: 'ProductRadioButtons-itemLabelText'
            }, [
                h('span', {
                    className: 'ProductRadioButtons-itemLabelTextName'
                }, labelText),
                isOnSale && sale.suffix && h('span', {
                    className: 'ProductRadioButtons-itemLabelTextSuffix'
                }, `${sale.suffix}`)
            ])));
        });
    };
    return h('div', {
        className: 'ProductRadioButtons'
    }, optionNames.map(function(optionName, optionIndex) {
        const optionGroup = optionValueGroups[optionIndex];
        if (optionGroup == null) {
            return null;
        }
        return h('fieldset', {
            key: kebabCase(uid + optionName),
            className: cls('ProductRadioButtons-group', {
                'has-singleOption': optionGroup.length === 1
            })
        }, h('legend', {
            className: cls('ProductRadioButtons-groupTitle', {
                'ProductRadioButtons-groupTitle--focused': optionIndex === focusedOptionIndex
            }),
            innerHTML: function() {
                if (optionName.toLowerCase() === 'size') {
                    return `
                <span class="ProductRadioButtons-label">${optionName}:</span>
                <span class="ProductRadioButtons-selected">
                  <span class="ProductRadioButtons-size">${selectedOptions[optionIndex] || ''}</span>
                </span>
                <span class="ProductRadioButtons-link" role="button" tabindex="0" id="js-size-guide">size guide</span>
              `;
                }
                return `
              <span class="ProductRadioButtons-label">${optionName}:</span>
              <span class="ProductRadioButtons-selected">${selectedOptions[optionIndex] || ''}</span>
            `;
            }()
        }), h('div', {
            className: 'ProductRadioButtons-groupItems',
            'data-simplebar': ''
        }, renderOptionGroup(optionGroup, optionIndex, optionName)));
    }), !isEgift && firstOptionWithoutSelectedValue && h('button', {
        type: 'button',
        id: 'js-select-option',
        className: cls('ProductForm-selectButton', 'o-button', 'js-product-options-action')
    }, optionLabels[firstOptionWithoutSelectedValue] || `Select ${firstOptionWithoutSelectedValue}`), !isEgift && firstOptionWithoutSelectedValue === null && isSelectedVariantAvailable && h('button', {
        id: 'js-add-to-cart',
        class: cls('ProductForm-submitButton', 'o-button', 'js-product-options-action'),
        name: 'add'
    }, 'Add to Cart'), h('button', {
        id: 'notify-me-btn',
        type: 'button',
        class: cls('o-button', 'ProductForm-bts', 'js-product-options-action', {
            'is-visible': firstOptionWithoutSelectedValue === null && !isSelectedVariantAvailable && isStoredSelectionValidForCurrentProduct,
            'is-hidden': !(firstOptionWithoutSelectedValue === null && !isSelectedVariantAvailable && isStoredSelectionValidForCurrentProduct)
        })
    }, 'NOTIFY ME WHEN AVAILABLE'), firstOptionWithoutSelectedValue === null && !isSelectedVariantAvailable && !isStoredSelectionValidForCurrentProduct && h('button', {
        type: 'button',
        disabled: true,
        className: cls('ProductForm-selectButton', 'o-button')
    }, 'Not Available'), !isEgift && stickyATCLabel && h('a', {
        href: '#js-product-details-anchor',
        id: 'js-sticky-atc',
        class: cls('o-button', 'ProductForm-stickyATC', {
            'is-sticky': isStickyATCVisible
        }, stickyATCExtraClass),
        onClick: function() {
            const activeButton = document.querySelector(ACTIVE_CTA_QUERY_SELECTOR);
            if (activeButton instanceof HTMLElement) {
                activeButton.click();
            }
        }
    }, stickyATCLabel));
};
